import { useState, type ReactNode } from "react";

import { ApiError } from "../../api/client.ts";
import { useCloseTrade } from "../../api/hooks.ts";
import {
  formatPercent,
  formatPips,
  formatR,
  formatSignedPips,
  labelOutcome,
  parseDecimal,
  toneOf,
} from "../../domain/format.ts";
import { closePreview, disagreement, typedClosePreview } from "../../domain/pips.ts";
import { Icon } from "../../ui/Icon.tsx";
import { MomentField, NumberField, Select } from "../../ui/Field.tsx";
import { SaveButton } from "../../ui/SaveButton.tsx";
import { Conflict, localNow } from "./TradeDetail.tsx";

/**
 * El cierre (SPEC 15.5, ADR 0007).
 *
 * La entrada primaria son LOS PIPS GANADOS O PERDIDOS, con signo: es lo que la
 * plataforma ensena al salir y lo que el propietario quiere apuntar. **El R se
 * calcula MIENTRAS escribes**, contra los pips de riesgo del plan, y ver el R
 * antes de guardar es lo que evita el dedazo.
 *
 * El precio de salida queda plegado y opcional. Si se escriben los dos y no
 * cuadran, se dice: ganan los pips escritos y la operacion queda senalada, sin
 * corregir nada en silencio. Lo que se ve aqui es una previsualizacion; la
 * verdad aritmetica la pone el servidor al responder (SPEC 15.1).
 */

const REASONS = [
  { value: "takeProfit", label: "Llego al objetivo" },
  { value: "stopLoss", label: "Llego al stop" },
  { value: "manual", label: "La cerre a mano" },
  { value: "trailingStop", label: "Stop dinamico" },
  { value: "breakEven", label: "Salto en el break-even" },
  { value: "timeStop", label: "Se acabo mi horario" },
  { value: "other", label: "Otro" },
];

const GRADES = [
  { value: "aPlus", label: "A+" },
  { value: "a", label: "A" },
  { value: "b", label: "B" },
  { value: "c", label: "C" },
  { value: "d", label: "D" },
];

type Trade = {
  id: string;
  version: number;
  direction: string;
  pipSizeUsed: number | null;
  plannedRiskPips: number;
  actualEntryPrice: number | null;
};

export function CloseTrade(props: { trade: Trade }): ReactNode {
  const { trade } = props;
  const close = useCloseTrade(trade.id);

  const [resultPips, setResultPips] = useState("");
  const [exitPrice, setExitPrice] = useState("");
  const [pricesOpen, setPricesOpen] = useState(false);
  const [reason, setReason] = useState("manual");
  const [mae, setMae] = useState("");
  const [mfe, setMfe] = useState("");
  const [grade, setGrade] = useState("");
  const [closedAt, setClosedAt] = useState(localNow());

  const error = close.error instanceof ApiError ? close.error : null;

  const typed = parseDecimal(resultPips);
  const exit = parseDecimal(exitPrice);
  const mfeValue = parseDecimal(mfe);
  const direction = trade.direction === "sell" ? "sell" : "buy";

  /* Lo que sale del precio, si hay con que: precio real de entrada y tamano de
     pip. Sin pips escritos es lo que se guarda; con ellos, es solo el contraste. */
  const derived =
    trade.actualEntryPrice !== null && trade.pipSizeUsed !== null && exit !== null
      ? closePreview(
          trade.actualEntryPrice,
          exit,
          direction,
          trade.pipSizeUsed,
          trade.plannedRiskPips,
          mfeValue,
        )
      : null;

  const preview =
    typed !== null ? typedClosePreview(typed, trade.plannedRiskPips, mfeValue) : derived;

  const verdict = disagreement(
    { plannedRiskPips: null, plannedRewardPips: null, resultPips: typed },
    { plannedRiskPips: null, plannedRewardPips: null, resultPips: derived?.resultPips ?? null },
  );

  /* Cerrar solo con el precio de salida exige el precio real de entrada: sin el
     no hay de donde derivar el resultado, y el servidor respondera 400. */
  const exitNeedsEntry = typed === null && exit !== null && trade.actualEntryPrice === null;

  return (
    <section className="panel" style={{ marginTop: 16 }}>
      <div className="panel-h">
        <div>
          <h3>Cerrar la operacion</h3>
          <p>Escribe los pips que ganaste o perdiste; el R sale solo</p>
        </div>
        <Icon name="check" />
      </div>

      <div className="panel-b">
        {error !== null && <Conflict error={error} />}

        <NumberField
          label="Pips ganados o perdidos"
          value={resultPips}
          onChange={setResultPips}
          placeholder="+180"
          error={error?.fieldError("resultPips")}
          hint={`Con signo: +180 si ganaste, -100 si perdiste. Contra los ${formatPips(trade.plannedRiskPips)} pips de riesgo del plan.`}
        />

        {preview !== null && (
          <div className="preview">
            <div className="preview-h">
              <Icon name="sparkles" />
              Previsualizacion · la calcula la app
            </div>
            <div className="preview-grid">
              <div>
                <small>R</small>
                <b className={toneOf(preview.rMultiple)}>{formatR(preview.rMultiple)}</b>
              </div>
              <div>
                <small>Resultado</small>
                <b className={toneOf(preview.resultPips)}>
                  {formatSignedPips(preview.resultPips)} pips
                </b>
              </div>
              <div>
                <small>Cierra como</small>
                <b>{labelOutcome(preview.outcome)}</b>
              </div>
              <div>
                <small>Captura</small>
                <b>{formatPercent(preview.captureRate)}</b>
              </div>
            </div>
            {preview.rMultiple === null && (
              <p className="hint">
                Esta operacion se planifico sin riesgo, asi que no tiene R: no es un cero.
              </p>
            )}
            {verdict.resultDisagree && derived !== null && typed !== null && (
              <p className="warnbox">
                <b>Los pips escritos y los que salen del precio no cuadran.</b> Del precio de
                salida salen {formatSignedPips(derived.resultPips)} pips y escribiste{" "}
                {formatSignedPips(typed)}. Uno de los dos es un dedazo: se guardan los escritos y
                la operacion queda senalada hasta que lo revises.
              </p>
            )}
          </div>
        )}

        <details
          className="fold"
          open={pricesOpen}
          onToggle={(event) => setPricesOpen(event.currentTarget.open)}
        >
          <summary>
            Precio de salida, si lo tienes
            <i className="opt">opcional</i>
          </summary>
          <div className="fold-b">
            <NumberField
              label="Precio real de salida"
              value={exitPrice}
              onChange={setExitPrice}
              error={error?.fieldError("actualExitPrice")}
              hint={
                trade.actualEntryPrice === null
                  ? "Esta operacion no tiene precio real de entrada, asi que de aqui no sale ningun pip: se guarda como contexto."
                  : "Se guarda como contexto. Si tambien escribes los pips, mandan los pips y la app avisa si no cuadran."
              }
            />
            {exitNeedsEntry && (
              <p className="warnbox">
                Sin precio real de entrada no hay de donde derivar el resultado. Escribe los pips
                ganados o perdidos arriba.
              </p>
            )}
          </div>
        </details>

        <div className="grid2">
          <MomentField label="Hora de cierre" value={closedAt} onChange={setClosedAt} />
          <Select
            label="Motivo de salida"
            value={reason}
            onChange={setReason}
            options={REASONS}
            error={error?.fieldError("exitReason")}
            placeholder="La cerre a mano"
          />
        </div>

        <div className="grid2">
          <NumberField
            label="MAE en pips"
            value={mae}
            onChange={setMae}
            error={error?.fieldError("maePips")}
            hint="Lo mas lejos que el precio fue EN CONTRA mientras la operacion estaba viva. Es una distancia: se escribe sin signo."
          />
          <NumberField
            label="MFE en pips"
            value={mfe}
            onChange={setMfe}
            error={error?.fieldError("mfePips")}
            hint="Lo mas lejos que el precio fue A FAVOR, tambien sin signo. No puede ser menor que los pips que ganaste: el precio llego al menos hasta donde saliste."
          />
        </div>

        <Select
          label="Nota de ejecucion"
          value={grade}
          onChange={setGrade}
          options={GRADES}
          error={error?.fieldError("executionGrade")}
          hint="El resultado y la calidad de la ejecucion son ejes independientes: una perdida puede ser una ejecucion A, y una ganancia por saltarte el plan es una D."
        />

        <SaveButton
          label="Cerrar la operacion"
          busy={close.isPending}
          saved={false}
          disabled={preview === null || exitNeedsEntry}
          onClick={() =>
            close.mutate({
              resultPips: typed,
              actualExitPrice: exit,
              closedAtUtc: new Date(closedAt).toISOString(),
              exitReason: reason,
              maePips: parseDecimal(mae),
              mfePips: mfeValue,
              executionGrade: grade === "" ? null : grade,
              version: trade.version,
            })
          }
        />
      </div>
    </section>
  );
}
